import { LitElement, html, css, unsafeCSS } from 'lit';
import { CHILD_TAGS, EVENTS, SELECTORS } from '../constants';
import styles from './radioGroup.scss?inline';

export default class SotbitRadioGroup extends LitElement {
    static styles = css`
        ${unsafeCSS(styles)}
    `;

    static formAssociated = true;

    static properties = {
        name: { type: String },
        value: { type: String, reflect: true },
    };

    constructor() {
        super();

        this.internals = this.attachInternals();
        this.name = '';
        this.value = null;
        this._defaultValue = null;
        this.radioOptions = [];
        this._form = null;

        this.onOptionChange = this.onOptionChange.bind(this);
    }

    get form() {
        return this._form;
    }

    firstUpdated() {
        this.handleSlotChange();
    }

    formAssociatedCallback(form) {
        this._form = form;
    }

    formDisabledCallback(disabled) {
        this.toggleAttribute('disabled', disabled);
    }

    formResetCallback() {
        this.value = this._defaultValue;
        let searchChecked = false;

        this.radioOptions.forEach((option) => {
            const checked = option.value === this.value && !searchChecked;
            option.checked = checked;
            option.toggleAttribute('checked', checked);

            if (checked) {
                searchChecked = true;
            }
        });
    }

    formStateRestoreCallback(state) {
        this.value = state;
    }

    handleSlotChange() {
        const slot = this.shadowRoot.querySelector(SELECTORS.SLOT);

        if (!slot) {
            return;
        }

        const assignedElements = slot.assignedElements({ flatten: true });
        this.radioOptions = assignedElements.filter((el) => {
            return Object.keys(CHILD_TAGS).find((key) => CHILD_TAGS[key] === el.tagName);
        });

        if (this.value === null) {
            const checkedOption = this.radioOptions.find((option) => option.checked);

            if (checkedOption) {
                this.value = checkedOption.value;

                if (this._defaultValue === null) {
                    this._defaultValue = this.value;
                }
            }
        }

        this.radioOptions.forEach((option) => {
            option.name = this.name;

            if (this.value) {
                option.checked = option.value === this.value;
            }

            option.removeEventListener(EVENTS.CHANGE, this.onOptionChange);
            option.addEventListener(EVENTS.CHANGE, this.onOptionChange);
        });
    }

    onOptionChange = (e) => {
        const selectedValue = e.currentTarget.value ?? e.detail.value;
        if (this.value === selectedValue) {
            return;
        }

        this.value = selectedValue;

        this.radioOptions.forEach((option) => {
            const checked = option.value === this.value;
            option.checked = checked;
            option.toggleAttribute('checked', checked);
        });

        this.internals.setFormValue(this.value);
    };

    render() {
        return html`
            <div role="radiogroup">
                <slot @slotchange=${this.handleSlotChange}></slot>
            </div>
        `;
    }
}
